// W12-2 — WHOSE NAME BELONGS ON A MAGIC ROOM OUTPUT, AND IT IS ALMOST NEVER THE MEMBER'S.
//
// HER RULING, VERBATIM IN INTENT: **remove the member's name and birthday from Magic Room outputs
// entirely — EXCEPT the Cauldron** (which assembles a working FOR a person) **and petitions** (which
// are FOR a named person by design). "Header blocks on herb/oil/spell views carry THE ITEM, not the
// member."
//
// A spell is not a reading about her. A herb is not about her. Printing "General Information: Name,
// Date of Birth, Place of Birth" above a spell says the working was cast from her chart, which it
// was not, and hands a member's identity to a page she may well print and leave lying about.
//
// ONE LAW, THREE SURFACES: the report view, the PDF, and the gate. Imports NOTHING, so
// `scripts/w12-magic-language.mjs` loads and RUNS it.

// The families the Magic Room delivers. Kept beside `MAGIC_ROOM_FAMILIES` in
// server/download-gating.js rather than merged with it, because that set answers "does this cost the
// Magic Room" and this one answers "whose document is it" — two questions that happen to be about the
// same rooms today and are not the same question.
// R28 — AND THREE OF THE ROOM'S OWN TYPES WERE NOT ON IT, WHICH MADE EVERY LAW BELOW ANSWER "NOT
// MINE" ABOUT THEM. `chant`, `affirmation` and `note` are filed to room "magic" by the server and
// were absent here, so `isMagicOutput` said false and the whole W12-2 stack read them as ordinary
// readings. The sharpest evidence that this was an omission rather than a decision: ONE call site
// (`server/index.js`, `type: kind`) writes chant, affirmation AND incantation from the same
// generator, and only `incantation` was on the roster — so the same drawer produced one working that
// kept the member private and two that printed her name and her date of birth.
//
// It cost her two things, neither hypothetical:
//   · `hidesMemberIdentity` answered false, so `NarrativeReportView` and `server/pdf.js` both drew
//     the General Information block — her name, her birthday, her birthplace — on a chant, an
//     affirmation and her own note. Her law is that EVERY family hides the member except the two
//     named exceptions below, and these are not among them.
//   · `magicNoun` answered "", so the body heading, both report modals, the download name and the
//     shelf card's empty-title fallback all said "Your reading" — the exact word her W12-2 order
//     forbids anywhere in this room.
// R37 — THE CONJURE STACK'S THREE DRAWERS, DECLARED ONCE, HERE, WHERE THE ROOM'S OWN LAW LIVES.
// The three of them are one generator (`server/index.js` writes `type: kind` from a single call
// site) and one component in App.tsx rendered three times with hand-typed props — and R28's defect
// is what that costs: a kind on the drawer and off this roster prints her name and her date of birth
// on the working. The words are HERS and are moved verbatim; what changed is that the KIND now has
// one home, so a fourth drawer joins every law below by EXISTING rather than by somebody remembering.
// 14-B7b: THE ROSTER KEEPS WHAT IS LOAD-BEARING AND THE WORDS MOVED TO THE CATALOG.
// `kind` is what the server registers and what four rosters must recognise; `tab` is the drawer's
// NAME, keyed on by `DRAWER_ICONS`, so a translated tab wears a generic sparkle (R25's live defect
// exactly) and it stays English with the rest of the right rail until that rail is its own round.
// The four SENTENCES are `src/strings.js` rows in both languages. They are NOT duplicated here as
// English: a second copy is a second answer key (R28/R29), so the roster carries the KEYS instead,
// written out as LITERALS because a key assembled from the kind is a key no grep, no gate and
// nobody renaming a row can find.
export const CRAFT_DRAWERS = [
  {
    kind: "chant", tab: "Chants",
    copy: { title: "drawer.chants.title", blurb: "drawer.chants.blurb", placeholder: "drawer.chants.placeholder", cta: "drawer.chants.cta" },
  },
  {
    kind: "affirmation", tab: "Affirmations",
    copy: { title: "drawer.affirmations.title", blurb: "drawer.affirmations.blurb", placeholder: "drawer.affirmations.placeholder", cta: "drawer.affirmations.cta" },
  },
  {
    kind: "incantation", tab: "Incantations",
    copy: { title: "drawer.incantations.title", blurb: "drawer.incantations.blurb", placeholder: "drawer.incantations.placeholder", cta: "drawer.incantations.cta" },
  },
];

export const CRAFT_KINDS = CRAFT_DRAWERS.map((d) => d.kind);

// R31's shape, one drawer deeper (moved out of App.tsx by ITEM 4(c), which had to pay for its own
// five lines): **THE TIER KEY IS THE DRAWER, so it is DERIVED from the kind rather than hand-typed a
// second time.** `CRAFT_FEATURE` used to be a `Record<"chant"|"affirmation"|"incantation", string>`
// sitting 300 lines from the tab that reads it, and its own comment said what was at stake — "a typo
// here is a drawer that opens for a tier that did not buy it". A derived key cannot have a typo in
// it, and the gates that used to parse that map out of a 13,000-line TSX file now IMPORT this.
export const craftFeature = (kind) => `drawer:${kind}s`;
export const CRAFT_FEATURES = Object.fromEntries(CRAFT_KINDS.map((k) => [k, craftFeature(k)]));

// Both spellings of each craft, because `magicFamily` strips a suffix but not a plural, and every
// other family on this roster is listed both ways.
const CRAFT_FAMILIES = CRAFT_KINDS.flatMap((k) => [k, `${k}s`]);

export const MAGIC_FAMILIES = [
  "spell", "spells", "oil", "oils", "herb", "herbs", "key", "keys", "moses",
  "seal", "seals", "cauldron", "petition", "petitions",
  // DERIVED (R31's shape: no second roster to keep in step). This is the exact line R28 had to fix
  // by hand, and hand is how it came to be wrong in the first place.
  ...CRAFT_FAMILIES,
  "note", "notes",
];

// THE TWO THAT KEEP THE MEMBER, and her reason for each is in her own order:
//   cauldron  — it assembles a working FOR a person, so who it is for is part of what it is.
//   petition  — a petition is addressed to a named person BY DESIGN.
export const IDENTITY_FAMILIES = ["cauldron", "petition", "petitions"];

// A report type is written as `spell-working`, `keys-working`, `cauldron-working`, `herb-report`…
// The suffix is stripped the same way `normalizeReportKind` strips it on the server, so one spelling
// of a family cannot be treated differently from another.
export function magicFamily(type) {
  return String(type || "").toLowerCase().trim().replace(/-(working|reading|report)$/, "");
}
export function isMagicOutput(type) {
  return MAGIC_FAMILIES.includes(magicFamily(type));
}

// **THE ONE QUESTION EVERY SURFACE ASKS.** True only for a Magic Room output that is not one of her
// two exceptions. Anything that is not a Magic Room output at all answers false, because this law
// has nothing to say about a birth chart — which of course carries her name and her birthday.
export function hidesMemberIdentity(type) {
  return isMagicOutput(type) && !IDENTITY_FAMILIES.includes(magicFamily(type));
}

// What the header block says instead. **THE ITEM, NOT THE MEMBER** — her sentence. The kicker a
// report already carries names the working, so it is used when there is one rather than inventing a
// second title that could disagree with it.
export function magicHeading(report = {}) {
  const kicker = String(report.kicker || "").trim();
  if (kicker) return kicker;
  const family = magicFamily(report.type);
  return ({
    spell: "A Spell", spells: "A Spell",
    oil: "A Spelled Oil", oils: "A Spelled Oil",
    herb: "An Herb", herbs: "An Herb",
    key: "A Working from the Greater Keys", keys: "A Working from the Greater Keys",
    moses: "A Working from the Books of Moses",
    incantation: "An Incantation", incantations: "An Incantation",
    seal: "A Seal", seals: "A Seal",
    cauldron: "A Working from the Cauldron",
    petition: "A Petition", petitions: "A Petition",
    // R28: the three that were missing, headed the way their sibling incantation is headed.
    chant: "A Chant", chants: "A Chant",
    affirmation: "An Affirmation", affirmations: "An Affirmation",
    note: "A Note", notes: "A Note",
  })[family] || "A Working";
}

// ---- W12-2, THE LANGUAGE HALF -----------------------------------------------------------------
//
// HER ORDER: **NOTHING IN THE MAGIC ROOM SAYS "READING."** Herbs, oils, spells and seals are not
// readings. The replacements are her own words, verbatim from the order: *your spell, your working,
// your oil, your petition, the herb*.
//
// A heading and a NOUN are two different jobs and that is why this table is not `magicHeading`.
// A heading titles the document ("A Working from the Cauldron"); a noun is what a viewer, a shelf
// card or a filename calls the thing in passing ("Your working"). One word said in three places has
// to come from one table, or the page, the shelf and the download drift apart the way the shelf
// label and the document heading already had.
// R28: EXPORTED, because `magicNoun` falls back to "Your working" for a family with no entry and a
// check on the RETURNED VALUE cannot tell a declared "Your working" (key, moses, cauldron — correct)
// from a family that fell through to it (R26's lesson: value equality cannot see that something
// stopped being derived). The gate asks this table directly for a key per family.
export const MAGIC_NOUNS = {
  spell: "Your spell", spells: "Your spell",
  oil: "Your oil", oils: "Your oil",
  herb: "The herb", herbs: "The herb",
  key: "Your working", keys: "Your working",
  moses: "Your working",
  incantation: "Your incantation", incantations: "Your incantation",
  seal: "Your seal", seals: "Your seal",
  cauldron: "Your working",
  petition: "Your petition", petitions: "Your petition",
  // R28: the three that were missing. `incantation` is their own sibling — the same generator writes
  // all three — so these follow its wording rather than inventing a register for them.
  chant: "Your chant", chants: "Your chant",
  affirmation: "Your affirmation", affirmations: "Your affirmation",
  note: "Your note", notes: "Your note",
};

// The noun for a Magic Room output, or "" for anything that is not one. Empty rather than a default
// is deliberate: a birth chart IS a reading, and this law must not be able to rename it.
export function magicNoun(type) {
  if (!isMagicOutput(type)) return "";
  return MAGIC_NOUNS[magicFamily(type)] || "Your working";
}

// **THE ONE CALL A SURFACE MAKES.** Her noun where there is one; the caller's own word where there
// is not, passed in rather than assumed, because "Your reading" and "Saved report" are both correct
// outside this room and neither is correct inside it.
export function outputNoun(type, fallback = "Your reading") {
  return magicNoun(type) || fallback;
}

// The SAME noun as a heading. Two casings, ONE table: a toolbar says "Your spell" in a sentence and
// the report body's `<h3>` is Title Case beside "Your Centers" and "Your Reading". Derived rather
// than typed out again, so the two can never come to mean different things.
export function magicNounTitle(type) {
  const noun = magicNoun(type);
  if (!noun) return "";
  return noun.split(" ").map((w) => w.charAt(0).toUpperCase() + w.slice(1)).join(" ");
}

// **THE BODY HEADING THAT WAS FOUND BY THE WALKTHROUGH AND NOT BY THE SWEEP.** Every narrative report
// on this site heads its body `<h3>Your Reading</h3>`. N1-1 had already carved ONE exception out by
// hand — the Cauldron passes "Your Spell" — and that hand-carved exception is exactly why the rest of
// the Magic Room kept the word: a herb view read "HERBS & TOOLS / Your Reading" over a paragraph
// about rue. Rendered, photographed, then fixed.
export function magicBodyHeading(type, fallback = "Your Reading") {
  return magicNounTitle(type) || fallback;
}

// R44 — THE TWO FORM ROSTERS THIS ROOM ASKS HER TO PICK FROM, AND BOTH WERE TYPED TWICE.
//
// A petition is invoked in a divine name SHE chooses, and an oil is made in a tradition SHE chooses.
// Both were an allow-list on the server with a SILENT FALLBACK — server/index.js's own comment says
// "an unknown submitted name falls back to El Shaddai", and the oil mode falls back to "hoodoo" the
// same way — with the SAME keys hand-typed again in App.tsx, eight thousand lines away, as the
// dropdown and the three cards she actually taps.
//
// **THE RECEIVING SIDE'S DEFAULT IS SILENCE, WHICH IS THE WHOLE COST.** A key on the client that the
// server does not hold is not an error and is not logged: she picks Allah and is handed a petition
// spoken in the name El Shaddai, or asks for the seal-centred conjure working and is handed a
// rootwork oil. Nothing on screen looks wrong, because the thing she asked for never appears
// anywhere in what comes back to say it is missing.
//
// R31's fix rather than R13's: the crossing STOPS EXISTING instead of being watched. One declaration,
// and the fallback is a named function both sides call, so "what may be picked" and "what happens to
// something else" are one answer in one place.

// MR-P2 — "Elohim Tzaabaoth" is HER spelling (verified in her materials and her `elohim-tzaabaoth`
// seal; the prompt's "Tzabaoth" is not how she spells it). Order is the order of the dropdown.
export const PETITION_DIVINE_NAMES = [
  "El Shaddai", "Yahweh", "Jesus", "Jehovah", "Elohim", "Elohim Gibor", "Elohim Tzaabaoth", "Allah",
];
export const PETITION_DEFAULT_DIVINE_NAME = "El Shaddai";
export function resolveDivineName(name) {
  const asked = String(name || "").trim();
  return PETITION_DIVINE_NAMES.includes(asked) ? asked : PETITION_DEFAULT_DIVINE_NAME;
}

// The three oil traditions, with the words the room offers them in. The LABEL and the NOTE live here
// beside the key on purpose: they are the only description of what each mode does that she ever
// reads, and a key whose meaning is written down somewhere the server cannot see is how a mode comes
// to mean two things.
export const OIL_TRADITIONS = [
  { key: "hoodoo", label: "Hoodoo", note: "A rootwork oil: herbs, a carrier oil, and the method." },
  { key: "hoodoo-conjure", label: "Hoodoo + Conjure", note: "The rootwork oil, with a Greater Keys of Solomon seal woven in." },
  { key: "conjure", label: "Conjure", note: "The Greater Keys seal at the center; the oil dresses and activates it." },
];
export const OIL_DEFAULT_TRADITION = "hoodoo";
export function resolveOilTradition(mode) {
  const asked = String(mode || "").trim();
  return OIL_TRADITIONS.some((t) => t.key === asked) ? asked : OIL_DEFAULT_TRADITION;
}

// R46 — THE THIRD FORM ROSTER IN THIS ROOM, AND THE ONLY ONE THAT WAS TYPED THREE TIMES.
//
// She chooses the sacred script her seal is inscribed in. What that choice MEANS was written out
// four times, in four files, as the same comparison against two string literals:
//
//   src/App.tsx                — the five buttons, and a hand-typed `ringStringsFor` computing the
//                                live count that decides whether the form refuses her seal
//   server/seal-submission.js  — the REAL `ringStringsFor`, which enforces the same cap
//   server/index.js            — `nonLatin` / `latinFamily`, deciding what the resolver is asked for
//
// **THE CLIENT'S COPY HAD ALREADY DRIFTED AND NOTHING COULD SEE IT.** The server trims and drops
// blanks before classifying; the client did neither, so three names in `["", "Hebrew"]` was THREE
// inscriptions on the server and SIX on the form. No datum the form can produce exercises that
// clause, so the two agreed on every input anyone ever tried — R45's lesson, that a rule is a
// FUNCTION and must be compared with probes that reach every clause, not with the corpus at hand.
//
// **AND THE SCRIPT WAS DECIDED BY `l === "Hebrew" || l === "Arabic"`.** Everything else was Latin by
// falling off the end of a comparison, so a SIXTH language added to the form would cost no ring
// string (her capacity count wrong in her favour, the ring filling past the point it renders) and
// the resolver would be told "Use LATIN letters only" — asked for glyphs no embedded font carries.
// Silence in all four places, the shape this room has now paid for three rounds running.
//
// So the ROSTER CARRIES THE SCRIPT. `sealScriptFor` is a lookup, not a comparison, and a language
// added to the form cannot fail to say what it is — the fixture then holds that script against the
// fonts this pipeline actually embeds and the resolver's own enum, so an unrenderable sixth language
// is RED rather than quietly Latin.
export const SEAL_LANGUAGES = [
  { label: "English", script: "latin" },
  { label: "Latin", script: "latin" },
  { label: "Hebrew", script: "hebrew" },
  { label: "Arabic", script: "arabic" },
  { label: "Spanish", script: "latin" },
];
// What a language nobody rostered is treated as, NAMED rather than reached by falling off the end of
// a comparison. It is the behaviour both sides always had; nothing on this path refuses an
// unrostered language today, and turning that into a refusal is a product decision, not a refactor.
export const SEAL_DEFAULT_SCRIPT = "latin";

export function sealScriptFor(language) {
  const asked = String(language || "").trim();
  return SEAL_LANGUAGES.find((l) => l.label === asked)?.script || SEAL_DEFAULT_SCRIPT;
}

// The DISTINCT scripts a submission asks the seal to carry — the Latin family shares one inscription
// however many Latin-family languages she taps, and each sacred script gets its own. Blank and
// whitespace entries are dropped HERE, once, which is the clause the client's copy never had.
export function sealScriptsFor(languages) {
  const list = (Array.isArray(languages) ? languages : [languages]).map((l) => String(l || "").trim()).filter(Boolean);
  const scripts = [...new Set(list.map(sealScriptFor))];
  return scripts.length ? scripts : [SEAL_DEFAULT_SCRIPT];
}

// The languages she picked, split the way the resolver prompt asks for them. Returns her LABELS (the
// prompt names the language to a model, not the script key), so "Set native to the PRIMARY non-Latin
// script" still reads Hebrew or Arabic.
export function sealLanguagesByScript(languages) {
  const list = (Array.isArray(languages) ? languages : [languages]).map((l) => String(l || "").trim()).filter(Boolean);
  return {
    nonLatin: list.filter((l) => sealScriptFor(l) !== SEAL_DEFAULT_SCRIPT),
    latinFamily: list.filter((l) => sealScriptFor(l) === SEAL_DEFAULT_SCRIPT),
  };
}

// S2.V — how many inscriptions this submission actually asks to be painted around one circumference.
// The measured cap (SEAL_CAPACITY.maxRingStrings) is expressed in THESE, not in names: a name in two
// scripts is two strings, and 12 names in English + Hebrew is 24, which reproducibly loses one.
export function ringStringsFor(nameCount, languages = []) {
  return nameCount * sealScriptsFor(languages).length;
}

// W13 (paying the ceiling R37's way: MOVE THE DATA) — WHAT EACH DRAWER OF THIS ROOM IS CALLED WHEN
// IT IS OPEN OVER THE SCENE.
//
// `tiles` and `spellbook` are deliberately EMPTY: those two are the room itself rather than a drawer
// over it, so the overlay wears no title. That is a real answer and not a missing one, which is why
// they are written down instead of left out — an absent key and a blank label are the same thing on
// screen and completely different things to read (R21/R32's lesson, one table over).
//
// It lived in App.tsx as `Record<MagicView, string>`, where the TYPE enforced totality. Out here the
// gate does it better: `magicOverlayViews()` is held against the room's real view roster, so a
// drawer added tomorrow is RED rather than silently unlabelled.
export const MAGIC_OVERLAY_LABELS = {
  tiles: "", spellbook: "",
  petition: "Petition", keys: "Greater Keys of Solomon", oils: "Spell Your Oils",
  moses: "The Books of Moses", herbs: "The Book of Roots", cauldron: "The Cauldron",
  notepaper: "Your Notepaper",
  // ITEM 12 — the file cabinet: the seals this house holds, in drawers, with nothing composed.
  cabinet: "The File Cabinet",
};
export function magicOverlayViews() { return Object.keys(MAGIC_OVERLAY_LABELS); }
export function magicOverlayLabel(view) {
  const key = String(view || "");
  return Object.prototype.hasOwnProperty.call(MAGIC_OVERLAY_LABELS, key) ? MAGIC_OVERLAY_LABELS[key] : "";
}

// ---- R50 — WHAT MAY BE SENT TO THE CAULDRON, AND WHAT IT IS CALLED WHEN SHE LOOKS IN THE POT -----
//
// R32's SHAPE WITH A WORSE FALLBACK. `KIND_LABEL` was a `Record<string,string>` hand-typed INSIDE
// `CauldronExperience`, nine rows mirroring `CAULDRON_KINDS` in server/index.js, ending in
// **`|| it.kind`** — so a kind on neither list did not fail, and did not fall back to a sentence: it
// showed the member THE RAW MACHINE VALUE in her own item list. R32 closed exactly this on
// `reportTypeLabel`, whose fallback at least collided with a real answer; here the fallback IS the
// machine.
//
// And the store had a way to produce one. `addCauldronItem` writes `String(kind || "working")`, and
// **"working" is not on either roster** — so a caller that names no kind (the Living Room's
// take-this-home door goes straight to the store, around the route that validates) puts a value in
// that column which the item list renders as the bare word `working`.
//
// So: ONE roster, imported by the route that admits a kind, by the store that writes one, and by the
// list that names one. It is a BICONDITIONAL against what the senders actually send — the gate reads
// every `kind` App.tsx hands `SendToCauldronButton`, plus the two hand-typed sends, and holds them to
// this list both ways.
//
// **THE WORDS STAY ENGLISH AND THAT IS DECLARED, NOT AN OVERSIGHT.** `src/strings.js` files
// `cauldronkind.` under ENGLISH_BY_DESIGN with the reason: these are the same names
// `REPORT_TYPE_LABELS` gives the same items on her shelf, `creationName.js` gives them when she has
// not named one, and the PDF kicker prints. Turning this list alone is the half-translation that
// layer exists to prevent, and turning all four is a round of its own. So they are LITERALS here,
// beside `OIL_TRADITIONS` and `MAGIC_OVERLAY_LABELS`, which are English for the same kind of reason.
// **THE DEFECT R50 CLOSED IS THE FALLBACK, NOT THE LANGUAGE.**
export const CAULDRON_KINDS = [
  { kind: "petition", label: "Petition" },
  { kind: "keys-working", label: "Greater Keys working" },
  { kind: "moses-working", label: "Books of Moses working" },
  { kind: "spell", label: "Spell" },
  { kind: "herb-working", label: "Herbs" },
  { kind: "oils", label: "Oils" },
  { kind: "oils-working", label: "Conjure Oil" },
  { kind: "seal", label: "Your seal" },
  { kind: "note", label: "Your words" },
  // CLOSET-1 (her order, 2026-08-14) — something she OWNS, off her own shelf. One declaration and
  // both sides have it: `server/index.js` builds its allow-list from `CAULDRON_KIND_KEYS`, so a kind
  // added here is a kind the route accepts and a kind it refuses is one nothing can send.
  { kind: "closet-item", label: "From your closet" },
];

// What may cross the wire. The route's allow-list and nothing else.
export const CAULDRON_KIND_KEYS = CAULDRON_KINDS.map((k) => k.kind);

// R29's OTHER SIDE, DECLARED RATHER THAN LEFT AS AN ABSENCE. This is what the STORE writes when a
// caller names no kind, and it is deliberately NOT on the sendable roster: widening the roster to
// cover it would let a client POST it. It has a label row of its own, so the one value the store can
// invent still reads as a sentence.
export const CAULDRON_FALLBACK_KIND = "working";
// R28's M5, avoided by construction: this is a sentence NO rostered kind also answers, so a check on
// the returned label can tell a fallthrough from a kind that meant it.
export const CAULDRON_FALLBACK_LABEL = "A working";

// TOTAL by construction: every input, rostered or not, resolves to words. There is no branch that
// can hand back the machine's own word, which is what `|| it.kind` was.
export function cauldronKindLabel(kind) {
  const asked = String(kind || "").trim();
  const row = CAULDRON_KINDS.find((k) => k.kind === asked);
  return row ? row.label : CAULDRON_FALLBACK_LABEL;
}

// The store's coercion, in ONE place, so "what ends up in that column" is a property of this file.
export function cauldronStoreKind(kind) {
  const asked = String(kind || "").trim();
  return CAULDRON_KIND_KEYS.includes(asked) ? asked : CAULDRON_FALLBACK_KIND;
}

// ─────────────────────────────────────────────────────────────────────────────
// R54 — WHAT THE SEAL FORM DOES WITH THE LINES SHE TYPES, AND WHY IT LIVES OUT HERE NOW.
//
// These three came out of `src/App.tsx` for the reason every extraction on roadmap 7 has: **a pure
// law inside a TSX file cannot be imported by a `.mjs` gate**, so they were held by regexes over a
// 13,000-line file — `scripts/gate.mjs` matched `SEAL_MAX_NAMES = 12` as TEXT, which proves the
// number is TYPED and nothing at all about what the form then counts. `ringStringsFor` (R46) is the
// precedent one function over: the count in front of her while she types and the count the server
// ENFORCES were two implementations and had already drifted.
//
// **THE CAPACITY NUMBERS ARE STILL THE SERVER'S** (`SEAL_CAPACITY` in `server/seal-submission.js`,
// which is what refuses for any caller). These are the form's copy of them, and the crossing is held
// by IMPORT on both sides rather than by a third copy typed into a fixture (R13's finding).

// S2.5 — the ceiling on names regardless of script count, so a single-script seal still has a bound.
export const SEAL_MAX_NAMES = 12;
export const SEAL_MAX_AFFIRMATIONS = 24;
// S2.V, MEASURED ON LIVE: the real constraint is INSCRIPTIONS ON THE RING, not names. Each name is
// painted once per script chosen, so 12 names in English + Hebrew is 24 inscriptions around one
// circumference, and the model reproducibly loses one (it rendered 11 of 12 pairs and dropped YHWH,
// four attempts running). No retry fixes a full ring.
export const SEAL_MAX_RING_STRINGS = 12;
// HER RULING 2026-08-04: 200 -> 600, so a verse fits without being cut in half.
//
// **THIS NUMBER IS NEW ON THE CLIENT AND ITS ABSENCE WAS A LIVE DEFECT (R15).** The form checked how
// MANY affirmations she wrote and never how LONG one was, so a 300-character verse was accepted by
// the field, posted, and refused by the server — the picker offering what the gate refuses, which
// costs her the whole submission before she is told no. The other three numbers were held equal to
// the server's by `gate.mjs` and `r54`; the fourth was not, which is exactly how it went unwatched.
export const SEAL_MAX_AFFIRMATION_LENGTH = 600;

// Which of her lines are too long to inscribe. Returned as the LINES rather than a count, because the
// refusal has to name the one she has to shorten — she may have written eight and one is over.
export function overlongAffirmations(lines, max = SEAL_MAX_AFFIRMATION_LENGTH) {
  return (Array.isArray(lines) ? lines : []).filter((a) => String(a ?? "").length > max);
}

// The catalog ARGUMENTS for the refusal, composed here rather than at the two call sites that need
// them (the submit guard and the counter under the box). R24's lesson: a block typed at more than one
// call site is held by nothing, and this one carries the numbers she has to act on. Returns null when
// there is nothing to refuse, so a caller can rank it above the count refusal by truthiness alone.
export function affTooLongArgs(lines, max = SEAL_MAX_AFFIRMATION_LENGTH) {
  const over = overlongAffirmations(lines, max);
  return over.length ? { line: over[0].slice(0, 40), max, count: over[0].length } : null;
}

// WHAT THE LINE UNDER THE AFFIRMATIONS BOX SAYS, as a decision rather than as a nested ternary inside
// a thirty-hook component. It was four arms deep in App.tsx, so nothing could run it — and the ORDER
// of those arms is a real ruling: **the LENGTH refusal outranks the COUNT one**, because an over-long
// line is the thing she has to act on and a member who is both over-count and over-length must be
// told about the line first. Returns the catalog KEY and its args (never composed copy), so the
// bilingual catalog stays the one place her words live. `null` means the box says nothing.
export function affirmationNote(lines, { max = SEAL_MAX_AFFIRMATION_LENGTH, maxCount = SEAL_MAX_AFFIRMATIONS } = {}) {
  const list = Array.isArray(lines) ? lines : [];
  const tooLong = affTooLongArgs(list, max);
  if (tooLong) return { key: "seal.affTooLong", args: tooLong, over: true };
  if (list.length > maxCount) return { key: "seal.overAffirmations", args: { max: maxCount, count: list.length }, over: true };
  if (list.length === 0) return { key: "seal.aff.empty", args: { max: maxCount }, over: false };
  return null;
}

// One per line. This is the STRUCTURE the S2 trace found missing: the form used to take names as ONE
// STRING and had no affirmations field at all (they were prose buried inside the intention), so
// nothing downstream could know how many names she meant or what her exact words were. You cannot
// verify a submission you never captured, and the gates were checking the model's list because the
// member's list did not exist as data.
export function splitSealLines(value) {
  return String(value || "").split("\n").map((v) => v.trim()).filter(Boolean);
}

// OWNER RULING 1 (2026-07-17): COLLAPSE TO ONE. Shown to her as she types, so the seal she is
// picturing is the seal she gets. **AFFIRMATIONS ARE NEVER DEDUPLICATED** — a repeated line may be
// exactly what she meant — which is why this is a names-only law and not a list helper.
//
// The server's `dedupeSealNames` is the same ruling on the same input and additionally RETURNS the
// duplicates, because it logs them. The two are held to one answer by import in the gate rather than
// folded together: the server's caller needs the dropped names and the form needs only the count it
// already computes from the lengths.
export function dedupeSealNameLines(names) {
  const seen = new Set();
  return names.filter((n) => {
    const key = String(n || "").toLowerCase().replace(/\s+/g, " ").trim();
    if (!key || seen.has(key)) return false;
    seen.add(key);
    return true;
  });
}

// >>> IT #146 — THE ANGELS SHE WAS RECOMMENDED RIDE INTO THE SEAL SHE IS BEING SENT TO BUILD. <<<
//
// HER TICKET (`daiwanalakia@gmail.com`): she describes her situation, the Angels tab lists the
// angels for it, she taps the control that offers to build a seal with them — and the drawer closes
// and **the angels are not carried over.** She asked whether that was intended. **HER RULING: YES,
// THEY CARRY.**
//
// **THE CAUSE WAS ONE LINE AND IT EXPLAINS BOTH HALVES OF HER REPORT.** The control was a bare
// `<a href={PAGE_PATHS.seals}>`, so the browser performed a FULL PAGE NAVIGATION: the whole app
// remounts (which is the drawer closing) and the drawer's `picks` — local state, and the only place
// those angels existed — are gone with it. Nothing was ever carried, because nothing ever carried it.
//
// **THIS TURNS HER PICKS INTO WHAT THE SEAL FORM TAKES, THROUGH THAT FORM'S OWN LAWS.** The names
// box is one-per-line and is read by `splitSealLines` + `dedupeSealNameLines`, so this composes with
// the SAME dedupe rather than a second spelling of it (R28/R29) — the copy that drifted would be the
// one deciding whether two angels with the same name reach her seal twice.
//
// >>> AND IT DOES NOT CAP, WHICH IS DELIBERATE (R17/R160: no silent caps). <<< `SEAL_MAX_NAMES` is
// 12 and the matcher returns between two and five by its own tool schema, so the cap is unreachable
// in practice — and where it ever were, **the seal room already refuses over it with its own
// sentence** (`seal.overNames`, naming the max and the count). Capping here would silently drop
// angels she was just shown, on the one surface whose whole complaint is that her angels went
// missing. One answer to "too many names", and it is the room's (R28/R29).
//
// An entry with no usable name contributes NOTHING rather than an empty line, because a blank line
// in that box is a name the form then has to refuse (`splitSealLines` drops it anyway, and doing it
// here means the COUNT she is shown is the count that arrives). Presence, never coercion
// (R53/R136/R137): only a real non-empty string is a name, so a payload field that is a number or an
// object contributes nothing rather than `[object Object]` on a seal.
export function angelsToSealNames(picks) {
  if (!Array.isArray(picks)) return [];
  const named = picks.map((p) => (p && typeof p.name === "string" ? p.name.trim() : "")).filter(Boolean);
  return dedupeSealNameLines(named);
}
